import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navigation from '../components/home/Navigation';
import Footer from '../components/home/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EvidenceLibraryPage = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndustry, setActiveIndustry] = useState('All');
  const [activeProblemType, setActiveProblemType] = useState('All');

  useEffect(() => {
    const fetchCases = async () => {
      try {
        const response = await axios.get(`${API}/case-studies`);
        setCases(response.data);
      } catch (err) {
        console.error('Failed to fetch case studies:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCases();
  }, []);

  // Extract unique industries and problem types
  const industries = ['All', ...new Set(cases.map(c => c.industry))];
  const problemTypes = ['All', ...new Set(cases.map(c => c.primary_problem_type))];

  // Filter cases
  const filtered = cases.filter(c => {
    const matchesSearch = !searchTerm || 
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.challenge_summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.quantified_outcomes.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.search_tags || []).some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesIndustry = activeIndustry === 'All' || c.industry === activeIndustry;
    const matchesProblem = activeProblemType === 'All' || c.primary_problem_type === activeProblemType;
    return matchesSearch && matchesIndustry && matchesProblem;
  });

  return (
    <div className="min-h-screen bg-white" data-testid="evidence-library-page">
      <Helmet>
        <title>Case Evidence Library | APIG Alliance</title>
        <meta name="description" content="Documented outcomes from high-stakes growth decisions. Each case represents a real engagement where predictive intelligence, strategic positioning, or growth activation produced measurable results." />
        <link rel="canonical" href="https://predictiveintelligencegrowthalliance.com/evidence" />
      </Helmet>
      <Navigation />

      {/* Header */}
      <section className="pt-32 pb-12 bg-white">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="overline mb-4">Case Evidence Library</p>
            <h1 className="heading-display text-4xl sm:text-5xl text-[#0B1A2B] mb-6" data-testid="library-headline">
              Documented Outcomes From High-Stakes Growth Decisions
            </h1>
            <p className="body-large">
              Each case represents a real engagement where predictive intelligence, strategic positioning, or growth activation produced measurable results.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-8 bg-white border-b border-[#E2E8F0]">
        <div className="container-luxury">
          <div className="max-w-5xl mx-auto">
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <input
                type="text"
                placeholder="Search cases by keyword, industry, or outcome..."
                className="input-editorial pl-11 w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="library-search"
              />
            </div>

            {/* Industry filter */}
            <div className="mb-4">
              <p className="text-xs text-[#64748B] uppercase tracking-wider font-medium mb-3">Industry</p>
              <div className="flex flex-wrap gap-2">
                {industries.map(ind => (
                  <button
                    key={ind}
                    onClick={() => setActiveIndustry(ind)}
                    className={`px-4 py-2 text-xs font-medium transition-all duration-200 ${
                      activeIndustry === ind
                        ? 'bg-[#0B1A2B] text-white'
                        : 'bg-[#F1F5F9] text-[#334155] hover:bg-[#E2E8F0]'
                    }`}
                    data-testid={`filter-industry-${ind.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {ind}
                  </button>
                ))}
              </div>
            </div>

            {/* Problem type filter */}
            <div>
              <p className="text-xs text-[#64748B] uppercase tracking-wider font-medium mb-3">Problem Type</p>
              <div className="flex flex-wrap gap-2">
                {problemTypes.map(pt => (
                  <button
                    key={pt}
                    onClick={() => setActiveProblemType(pt)}
                    className={`px-4 py-2 text-xs font-medium transition-all duration-200 ${
                      activeProblemType === pt
                        ? 'bg-[#0B1A2B] text-white'
                        : 'bg-[#F1F5F9] text-[#334155] hover:bg-[#E2E8F0]'
                    }`}
                    data-testid={`filter-problem-${pt.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {pt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12 bg-white">
        <div className="container-luxury">
          <div className="max-w-5xl mx-auto">
            <p className="text-sm text-[#64748B] mb-8" data-testid="results-count">
              {filtered.length} case{filtered.length !== 1 ? 's' : ''} found
            </p>

            {loading ? (
              <div className="text-center py-16">
                <p className="text-[#64748B]">Loading case evidence...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-[#334155] font-serif text-xl mb-2">No matching cases found.</p>
                <p className="text-[#64748B] text-sm">Try adjusting your filters or search terms.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {filtered.map((c, index) => (
                  <div
                    key={c.case_id}
                    className="p-8 bg-white border border-[#E2E8F0] transition-all duration-300 hover:shadow-lg hover:border-[#D97706]/30"
                    data-testid={`case-card-${c.case_id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                      {/* Left: Content */}
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <span className="text-[10px] text-[#D97706] uppercase tracking-wider font-medium">{c.industry}</span>
                          <span className="text-[#E2E8F0]">|</span>
                          <span className="text-[10px] text-[#64748B] uppercase tracking-wider">{c.primary_problem_type}</span>
                        </div>
                        <h3 className="font-serif text-xl lg:text-2xl text-[#0B1A2B] mb-3 leading-tight">{c.title}</h3>
                        <p className="text-[#334155] text-[0.9375rem] leading-relaxed mb-4">{c.challenge_summary}</p>
                        
                        {c.approach_summary && (
                          <div className="mb-4">
                            <p className="text-xs text-[#64748B] uppercase tracking-wider font-medium mb-1">Approach</p>
                            <p className="text-[#334155] text-sm leading-relaxed">{c.approach_summary}</p>
                          </div>
                        )}

                        {/* Tags */}
                        {c.search_tags && c.search_tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-4">
                            {c.search_tags.slice(0, 6).map((tag, i) => (
                              <span key={i} className="px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] text-[10px]">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Right: Outcomes + Download */}
                      <div className="lg:w-72 shrink-0">
                        <div className="p-5 bg-[#F8FAFC] border-l-[3px] border-[#D97706]">
                          <p className="text-[10px] text-[#D97706] uppercase tracking-wider font-medium mb-3">{c.outcome_type}</p>
                          <p className="text-[#0B1A2B] text-sm font-medium leading-relaxed">{c.quantified_outcomes}</p>
                        </div>
                        <a
                          href={`${API}/case-studies/${c.case_id}/pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0B1A2B] text-white text-xs font-medium tracking-wide uppercase hover:bg-[#D97706] transition-colors duration-200"
                          data-testid={`download-case-${c.case_id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download Case Study
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F1F5F9]">
        <div className="container-luxury text-center">
          <h2 className="font-serif text-3xl text-[#0B1A2B] mb-4">Have a similar challenge?</h2>
          <p className="text-[#334155] mb-8 max-w-lg mx-auto">
            Bring us the challenge before you place the bet. Our leadership team will match your situation to relevant evidence and reach out within 48 hours.
          </p>
          <Button
            className="btn-primary rounded-none px-8 py-4"
            onClick={() => window.location.href = '/#contact'}
            data-testid="library-cta"
          >
            Submit Your Challenge
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EvidenceLibraryPage;
